import { promotionFilter } from '@/helper/promotionFilter.js'

// Default initialstate
const initialState = {
  targetTariff: null,
  targetTariffoptions: [],
  targetPromotions: [],
  preSelectedOption: null,
  selectedTariffOptionIds: [],
  voucherCode: null,
  voucherPromotions: [],
  promoBenefits: {
    promoCodeVolume: {
      volume: {
        promoAmount: 0,
        unit: null
      },
      limitedVolume: {
        promoAmount: 0,
        duration: 0,
        durationUnit: null,
        unit: null
      }
    },
    volume: {
      amount: 0,
      promoAmount: 0,
      unit: null
    },
    limitedVolume: {
      promoAmount: 0,
      duration: 0,
      durationUnit: null,
      unit: null
    },
    bucketVolume: {
      promoAmount: 0,
      unit: null,
      duration: 0,
      durationUnit: null
    },
    price: {
      amount: 0
    },
    promoPrice: {
      promoAmount: 0,
      duration: 0
    },
    promoPriceDiscount: 0,
    additionalPriceStairways: [],
    startingBonus: 0
  }
}

// Getters for default initialState
const stateGetter = {
  targetTariff: state => state.targetTariff,
  targetPromotions: state => promotionFilter(state.targetPromotions), // apply promotion filter
  targetTariffoptions: state => state.targetTariffoptions,
  selectedTariffOptionIds: state => state.selectedTariffOptionIds,
  preSelectedOption: state => state.preSelectedOption,
  voucherCode: state => state.voucherCode,
  voucherPromotions: state => state.voucherPromotions,
  promoBenefits: state => state.promoBenefits
}
// Mutations for default initialState
const stateMutations = {
  setTargetTariff (state, targetTariff) { state.targetTariff = targetTariff },
  setTargetPromotions (state, targetPromotions) { state.targetPromotions = targetPromotions },
  setTargetTariffoptions (state, targetTariffoptions) { state.targetTariffoptions = targetTariffoptions },
  setSelectedTariffoptionIds (state, optionIds = []) {
    state.selectedTariffOptionIds = optionIds
  },
  setPreSelectedOption (state, optionId) {
    state.preSelectedOption = optionId
  },
  setVoucherCode (state, voucherCode) { state.voucherCode = voucherCode },
  setVoucherPromotions (state, voucherPromotions) { state.voucherPromotions = voucherPromotions },
  setPromoBenefits (state, promoBenefits) { state.promoBenefits = promoBenefits }
}

const calculatePromoBenefits = ({ tariff, tariffoptions, promotions }) => {
  const initCopy = JSON.parse(JSON.stringify(initialState)).promoBenefits
  const targetTariff = tariff
  const targetTariffoptions = Array.isArray(tariffoptions) ? tariffoptions : []
  const targetPromotions = promotionFilter(promotions) // apply promotion filter
  // Promo Variables
  const newPrice = initCopy.price
  const newVolume = initCopy.volume
  const newLimitedVolume = initCopy.limitedVolume
  const newBucketVolume = initCopy.bucketVolume
  const newPromoCodeVolume = initCopy.promoCodeVolume
  let newPromoPrice = initCopy.promoPrice
  let promoPriceDiscount = initCopy.promoPriceDiscount
  const additionalPriceStairways = initCopy.additionalPriceStairways
  let newStartingBonus = 0

  // Initialize values with TargetTariff
  newVolume.amount += targetTariff.volumes[0].amount
  newVolume.promoAmount += targetTariff.volumes[0].amount
  newVolume.unit = targetTariff.volumes[0].formattedUnit || 'GB'
  newLimitedVolume.unit = targetTariff.volumes[0].formattedUnit || 'GB'
  newBucketVolume.unit = targetTariff.volumes[0].formattedUnit || 'GB'
  newPrice.amount += targetTariff.basicFee
  newPromoPrice.promoAmount += targetTariff.basicFee

  // Add Tariffoptions
  if (targetTariffoptions && targetTariffoptions.length > 0) {
    targetTariffoptions.forEach(option => {
      newPrice.amount += option.price
      newPromoPrice.promoAmount += option.price
      if (option.volumes && option.volumes.length > 0) {
        option.volumes.forEach(volume => {
          if (volume.unit === newVolume.unit) {
            newVolume.amount += volume.amount
            newVolume.promoAmount += volume.amount
          }
        })
      }
    })
  }

  // Add Promotions
  targetPromotions.forEach(promo => {
    promo.incentives.forEach(incentive => {
      if (promo.duration && promo.duration.amount && promo.duration.amount > 0 && promo.duration.type === 'BUCKET') {
        // Bucket Promotions - we currently support no stairways for bucket promotions
        if (incentive.type === 'volume' && (newBucketVolume.duration === 0 || newBucketVolume.duration === promo.duration.amount) && newBucketVolume.unit === incentive.unit) {
          newBucketVolume.duration = promo.duration.amount
          newBucketVolume.durationUnit = promo.duration.formattedUnit
          newBucketVolume.promoAmount += incentive.amount
        }
      } else if (promo.duration && promo.duration.amount && promo.duration.amount > 0) {
        // Limited Promotions
        if (incentive.type === 'volume' && newLimitedVolume.unit === incentive.unit) {
          if (promo.requiredParameters && promo.requiredParameters.includes('promoCode')) {
            newPromoCodeVolume.limitedVolume.duration = newPromoCodeVolume.limitedVolume.duration === 0 ? promo.duration.amount : Math.min(promo.duration.amount, newPromoCodeVolume.limitedVolume.duration)
            newPromoCodeVolume.limitedVolume.durationUnit = promo.duration.formattedUnit
            newPromoCodeVolume.limitedVolume.promoAmount += incentive.amount
          } else {
            newLimitedVolume.duration = newLimitedVolume.duration === 0 ? promo.duration.amount : Math.min(promo.duration.amount, newLimitedVolume.duration)
            newLimitedVolume.durationUnit = promo.duration.formattedUnit
            newLimitedVolume.promoAmount += incentive.amount
          }
        } else if (incentive.type === 'credit') {
          const promoDuration = promo.duration.amount + 1
          // Stairway Handling
          if (additionalPriceStairways.length === 0) {
            // First stairway found
            additionalPriceStairways.push({
              duration: promoDuration,
              durationUnit: promo.duration.formattedUnit,
              promoAmount: newPromoPrice.promoAmount > incentive.discount ? newPromoPrice.promoAmount - incentive.discount : 0
            })
          } else {
            // More stairways found
            let stairwayAdded = false
            additionalPriceStairways.forEach((stairway, index) => {
              if (stairwayAdded) { return } // Stop processing if already added
              if (stairway.duration < promoDuration) {
                // Add amount to shorter stairways and continue processing
                stairway.promoAmount = stairway.promoAmount > incentive.discount ? stairway.promoAmount - incentive.discount : 0
              } else if (stairway.duration === promoDuration) {
                // Add amount to stairway with same duration and stop processing
                stairway.promoAmount = stairway.promoAmount > incentive.discount ? stairway.promoAmount - incentive.discount : 0
                stairwayAdded = true
              } else if (stairway.duration > promoDuration) {
                // Add new stairway before current element and based on the values of the current element and stop processing
                additionalPriceStairways.splice(index, 0, {
                  duration: promoDuration,
                  durationUnit: promo.duration.formattedUnit,
                  promoAmount: stairway.promoAmount > incentive.discount ? stairway.promoAmount - incentive.discount : 0
                })
                stairwayAdded = true
              }
            })
            if (!stairwayAdded) {
              // if stairway was not added until here, it is the new end element and based on regular price
              additionalPriceStairways.push({
                duration: promoDuration,
                durationUnit: promo.duration.formattedUnit,
                promoAmount: newPromoPrice.promoAmount > incentive.discount ? newPromoPrice.promoAmount - incentive.discount : 0
              })
            }
          }
        }
      } else {
        // Unlimited
        if (incentive.type === 'volume' && newVolume.unit === incentive.unit) {
          if (promo.requiredParameters && promo.requiredParameters.includes('promoCode')) {
            newPromoCodeVolume.volume.promoAmount += incentive.amount
          } else {
            newVolume.promoAmount += incentive.amount
          }
        } else if (incentive.type === 'credit' && incentive.targetFee === 'MONTHLY_FEE') {
          newPromoPrice.promoAmount = newPromoPrice.promoAmount > incentive.discount ? newPromoPrice.promoAmount - incentive.discount : 0
          promoPriceDiscount -= incentive.discount
          additionalPriceStairways.forEach(stairway => {
            stairway.promoAmount = stairway.promoAmount > incentive.discount ? stairway.promoAmount - incentive.discount : 0
          })
        } else if (incentive.type === 'credit' && incentive.targetFee === 'ONE_TIME_FEE') {
          newStartingBonus += incentive.discount
        }
      }
    })
  })

  // Reorganize Stairways for frontend
  if (additionalPriceStairways.length > 0) {
    const finalPromoAmount = newPromoPrice.promoAmount
    newPromoPrice = additionalPriceStairways.shift()
    let duration = newPromoPrice.duration
    let durationUnit = newPromoPrice.durationUnit
    additionalPriceStairways.forEach(stairway => {
      let tmp = stairway.duration
      stairway.duration = duration
      duration = tmp
      tmp = stairway.durationUnit
      stairway.durationUnit = durationUnit
      durationUnit = tmp
    })
    additionalPriceStairways.push({
      duration: duration,
      durationUnit: durationUnit,
      promoAmount: finalPromoAmount
    })
  }
  return {
    volume: newVolume,
    limitedVolume: newLimitedVolume,
    bucketVolume: newBucketVolume,
    price: newPrice,
    promoPrice: newPromoPrice,
    additionalPriceStairways: additionalPriceStairways,
    startingBonus: newStartingBonus,
    promoPriceDiscount: promoPriceDiscount,
    promoCodeVolume: newPromoCodeVolume
  }
}

export { initialState, stateGetter, stateMutations, calculatePromoBenefits }
